import {
  assignmentFilterBodyTypes,
  assignmentSearchContractorsBodyType,
  assignmentSearchRidesBodyType,
} from '@store/rtk/assignment';
import { idType } from '@src/types';
import { format, isAfter, isValid, parse, startOfDay } from 'date-fns';
import { UNASSIGNED_CONTRACTOR_IDS } from './constants';
import { allStatuses } from '@utils/helpers';
import { WEB_USER } from '@routes/helpers';

const pickedUpStatuses = [6, 7];
const REASSIGN_BLOCKED_STATUS_GROUPS = new Set([
  'Canceled',
  'Incomplete',
  'Rejected',
]);
const LOCKED_TRANSMIT_STATUS_GROUPS = new Set([
  'Canceled',
  'Incomplete',
  'Unassigned',
  'Rejected',
]);
const AUTO_SELECTED_TRANSMIT_STATUS_GROUPS = new Set([
  'Canceled',
  'Incomplete',
  'Rejected',
]);

export const isLockedTransmitRide = (ride: Record<string, any>) => {
  const statusConfig = allStatuses[Number(ride.statusId)];

  return Boolean(
    statusConfig && LOCKED_TRANSMIT_STATUS_GROUPS.has(statusConfig.group)
  );
};

export const isAutoSelectedTransmitRide = (ride: Record<string, any>) => {
  const statusConfig = allStatuses[Number(ride.statusId)];

  return Boolean(
    statusConfig && AUTO_SELECTED_TRANSMIT_STATUS_GROUPS.has(statusConfig.group)
  );
};

export const normalizeAssignmentDate = (value: unknown): string => {
  if (value instanceof Date && isValid(value)) {
    return format(value, 'MM/dd/yyyy');
  }

  if (typeof value !== 'string') {
    return format(Date.now(), 'MM/dd/yyyy');
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return format(parse(value, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy');
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return value;
  }

  return format(Date.now(), 'MM/dd/yyyy');
};

export const toIsoDateOfService = (value: unknown): string => {
  const normalized = normalizeAssignmentDate(value);
  const parsedDate = parse(normalized, 'MM/dd/yyyy', new Date());

  return isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd') : normalized;
};

export const isAssignmentDateTodayOrPast = (value: unknown): boolean => {
  const normalized = normalizeAssignmentDate(value);
  const parsedDate = parse(normalized, 'MM/dd/yyyy', new Date());

  if (!isValid(parsedDate)) {
    return false;
  }

  return !isAfter(startOfDay(parsedDate), startOfDay(new Date()));
};

export const normalizeAssignmentTime = (
  value: unknown,
  fallback: string
): string => {
  if (typeof value !== 'string' || !/^\d{2}:\d{2}$/.test(value)) {
    return fallback;
  }

  const [hours, minutes] = value.split(':').map(Number);

  return hours <= 23 && minutes <= 59 ? value : fallback;
};

export const getAssignmentContractorSearchBody = (
  filters: assignmentFilterBodyTypes,
  query = '',
  rideQuery = ''
): assignmentSearchContractorsBodyType => ({
  rideRequestDate: normalizeAssignmentDate(filters.rideRequestDate),
  startDate: normalizeAssignmentTime(filters.startDate, '00:00'),
  endDate: normalizeAssignmentTime(filters.endDate, '23:59'),
  includeIncompleteRides: filters.includeIncompleteRides ?? false,
  onlyWithRides: filters.onlyWithRides ?? false,
  ...(filters.stateId ? { stateId: filters.state } : {}),
  query,
  rideQuery,
});

export const getAssignmentRideSearchBody = (
  filters: assignmentFilterBodyTypes,
  contractorId: idType,
  query = '',
  rideQuery = ''
): assignmentSearchRidesBodyType => {
  const body: assignmentSearchRidesBodyType = {
    ...getAssignmentContractorSearchBody(filters, query, rideQuery),
  };

  if (contractorId !== null && contractorId !== undefined) {
    body.contractorId = contractorId;
  }

  if (typeof filters.state === 'number') {
    body.stateId = filters.state;
  }

  return body;
};

export type ContractorLockStatus = 'Locked' | 'Accepted' | 'Unlocked' | null;

export type LockableContractor =
  | {
      id?: idType;
      name?: string;
      locked?: boolean;
      accepted?: boolean;
    }
  | null
  | undefined;

type ContractorSocketStatusEntry = {
  isLocked?: string;
  lockedBy?: string;
};

export type ContractorSocketStatusMap = Record<
  string,
  ContractorSocketStatusEntry | undefined
>;

const isSameContractor = (
  a: LockableContractor,
  b: LockableContractor
): boolean =>
  Boolean(
    a?.id !== null && a?.id !== undefined && b && String(a.id) === String(b.id)
  );

export const getContractorLockStatus = (
  contractor: LockableContractor,
  socketContractorStatus?: ContractorSocketStatusMap
): ContractorLockStatus => {
  if (!contractor) {
    return null;
  }

  const { locked, accepted, id } = contractor;
  const backendLockStatus: ContractorLockStatus =
    locked && accepted
      ? 'Accepted'
      : locked && !accepted
        ? 'Locked'
        : locked !== null && !locked
          ? 'Unlocked'
          : null;

  const socketStatus =
    (socketContractorStatus?.[String(id)]?.isLocked as ContractorLockStatus) ??
    null;

  return socketStatus ?? backendLockStatus;
};

export const getContractorLockedByName = (
  contractor: LockableContractor,
  socketContractorStatus?: ContractorSocketStatusMap
): string => {
  if (!contractor) {
    return '';
  }

  return (
    socketContractorStatus?.[String(contractor.id)]?.lockedBy || ''
  ).trim();
};

export const buildLockedByMessage = (name: string): string =>
  // TODO remove this comment if the message is correct!!
  // `This contractor is locked by: ${name || 'another user'}.`;
  `The contractor is locked for the selected date of service. No additional rides can be assigned.`;

export const getContractorSourceLockMessage = (
  sourceContractor: LockableContractor,
  socketContractorStatus?: ContractorSocketStatusMap
): string => {
  if (!sourceContractor) {
    return '';
  }

  const status = getContractorLockStatus(
    sourceContractor,
    socketContractorStatus
  );

  if (status === 'Locked' || status === 'Accepted') {
    return buildLockedByMessage(
      getContractorLockedByName(sourceContractor, socketContractorStatus)
    );
  }

  return '';
};

export type ContractorAssignBlock = {
  contractor: LockableContractor;
  message: string;
  silent: boolean;
  allowNameLookup?: boolean;
};

export const getContractorAssignBlock = (
  sourceContractor: LockableContractor,
  targetContractor: LockableContractor,
  socketContractorStatus?: ContractorSocketStatusMap
): ContractorAssignBlock | null => {
  if (
    !targetContractor ||
    isSameContractor(sourceContractor, targetContractor)
  ) {
    return null;
  }

  const sourceMessage = getContractorSourceLockMessage(
    sourceContractor,
    socketContractorStatus
  );

  if (sourceMessage) {
    return {
      contractor: sourceContractor ?? null,
      message: sourceMessage,
      silent: true,
      allowNameLookup: true,
    };
  }

  const targetLockStatus = getContractorLockStatus(
    targetContractor,
    socketContractorStatus
  );

  if (targetLockStatus === 'Locked' || targetLockStatus === 'Accepted') {
    return {
      contractor: targetContractor,
      message: buildLockedByMessage(
        getContractorLockedByName(targetContractor, socketContractorStatus)
      ),
      silent: false,
      allowNameLookup: true,
    };
  }

  return null;
};

const getReassignBlockedStatus = (row: Record<string, any>) => {
  const group = allStatuses[Number(row?.statusId)]?.group;
  return REASSIGN_BLOCKED_STATUS_GROUPS.has(group) ? group : null;
};

export const rowIsBlocked = (row: Record<string, any>) =>
  Boolean(getReassignBlockedStatus(row));

export const rowIsBlockedMessage = (row: Record<string, any>): string => {
  const status = getReassignBlockedStatus(row);
  if (!status) return '';

  const confirmationCode = row.rideOrderConfirmationCode ?? row.ride;
  const segmentNumber = row.segmentSequence ?? row.segment;
  return `This segment (${confirmationCode} - ${segmentNumber}) cannot be reassigned because its status is ${status}.`;
};

export const isAssignmentRowBlockedFromContext = (
  rows: Record<string, any>[]
): string => {
  for (const row of rows) {
    const message = rowIsBlockedMessage(row);
    if (message) {
      return message;
    }
  }
  return '';
};

export const isAssignmentRowPickedUp = (row: Record<string, any>): boolean => {
  const statusId = Number(row?.statusId);
  if (Number.isFinite(statusId)) return pickedUpStatuses.includes(statusId);

  const label = String(row?.status ?? '')
    .trim()
    .toLowerCase();

  return (
    label.includes('enroute to drop') ||
    label.includes('en route to drop') ||
    label.includes('departed from drop')
  );
};

export const isAssignmentRowCompleted = (row: Record<string, any>): boolean => {
  const statusId = Number(row?.statusId);
  if (Number.isFinite(statusId)) return statusId === 12;

  const label = String(row?.status ?? '')
    .trim()
    .toLowerCase();

  return label.includes('verified complete') || label === 'completed';
};

export const isAssignmentRowAssignBlocked = (
  row: Record<string, any>
): boolean => {
  return isAssignmentRowPickedUp(row) || isAssignmentRowCompleted(row);
};

export const getAssignmentBlockedMessage = (
  row: Record<string, any>,
  segmentDetailData?: Record<string, any> | null
): string => {
  const rideId = row.rideOrderConfirmationCode ?? row.ride ?? '';
  const memberName = row.member ?? '';
  const memberIdNumber =
    (segmentDetailData && String(segmentDetailData.id) === String(row.id)
      ? segmentDetailData.member?.memberIdNumber
      : null) ??
    row.memberIdNumber ??
    row.memberId ??
    '';
  const details = [rideId, memberName, memberIdNumber]
    .filter(Boolean)
    .join(' - ');

  return `This segment cannot be moved because the member has already been picked up${details ? ': ' + details : '.'}`;
};

export const getAssignmentPickedUpMessage = getAssignmentBlockedMessage;

export const isUnassignedContractor = (contractor: {
  id: idType;
  name?: string;
}) => {
  return (
    UNASSIGNED_CONTRACTOR_IDS.has(String(contractor.id)) ||
    contractor.name?.trim().toLowerCase() === 'unassigned'
  );
};

export const isSystemContractor = (contractor: { name: string }) => {
  return (
    contractor.name === 'Unassigned' ||
    contractor.name === 'Contractor Rejected' ||
    contractor.name === 'Ride MarketPlace'
  );
};

export const isRideshareContractor = (contractor?: { type?: string } | null) =>
  contractor?.type?.trim().toLowerCase() === WEB_USER;

export const scrollSelectedContractorIntoView = (
  list: HTMLElement | null,
  selectedItem: HTMLElement | null
) => {
  if (!list || !selectedItem) {
    return;
  }

  const listRect = list.getBoundingClientRect();
  const selectedItemRect = selectedItem.getBoundingClientRect();

  if (selectedItemRect.top < listRect.top) {
    list.scrollTop -= listRect.top - selectedItemRect.top;
  } else if (selectedItemRect.bottom > listRect.bottom) {
    list.scrollTop += selectedItemRect.bottom - listRect.bottom;
  }
};
